// Wave 3.2 — Public state / service-area detail page.
// Loads /api/public/website/service-areas/:slug + all services and renders
// state coverage summary, applicable services, and a CTA to /quote?state=<slug>.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PublicShell, { CapabilityTile, usePublicContent } from "../../components/public/PublicShell";
import SEOHead, { buildServiceAreaSchema } from "../../components/public/SEOHead";
import { api } from "../../lib/api";
import { MapPin, Phone, Mail } from "lucide-react";

export default function ServiceAreaDetail() {
  const { slug } = useParams();
  const [area, setArea] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [services, setServices] = useState([]);
  const content = usePublicContent();

  useEffect(() => {
    api.get(`/public/website/service-areas/${slug}`).then((r) => setArea(r.data)).catch(() => setNotFound(true));
    api.get("/public/website/services").then((r) => setServices(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <PublicShell>
        <section className="erf-public-section">
          <div className="erf-public-container">
            <h1>Service area not found.</h1>
            <p style={{ color: "var(--exec-body)" }}>The state page you're looking for doesn't exist.</p>
            <Link to="/service-areas" className="erf-public-cta" style={{ marginTop: 20, display: "inline-block" }}>Back to service areas</Link>
          </div>
        </section>
      </PublicShell>
    );
  }
  if (!area) {
    return <PublicShell><section className="erf-public-section"><div className="erf-public-container">Loading…</div></section></PublicShell>;
  }

  const featured = area.featured_services && area.featured_services.length > 0
    ? services.filter((s) => area.featured_services.includes(s.slug))
    : services.slice(0, 6);

  const hero = (
    <section className="erf-public-hero" style={{ padding: "72px 0 56px" }} data-testid={`state-hero-${slug}`}>
      <div className="erf-public-container">
        <div className="erf-public-hero-eyebrow">Service Area · {area.state_code}</div>
        <h1 style={{ maxWidth: 900 }}>{area.state_name}</h1>
        <p className="lead">{area.coverage_summary}</p>
        <div className="erf-public-hero-actions">
          <Link to={`/quote?state=${area.state_code}`} className="erf-public-cta" data-testid="state-cta-quote">
            Request a Quote for a Project in {area.state_name}
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <SEOHead
        title={`${area.state_name} General Contractor — ERF ENTERPRISES LLC`}
        description={area.coverage_summary}
        jsonLd={buildServiceAreaSchema({ area })}
      />

      <section className="erf-public-section" data-testid="state-body">
        <div className="erf-public-container" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60 }}>
          <div>
            <div className="erf-public-eyebrow">Coverage</div>
            <p style={{ fontSize: 16, lineHeight: 1.75, color: "var(--exec-body)", marginTop: 12 }}>
              {area.coverage_summary}
            </p>
            {area.counties && area.counties.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div className="erf-public-eyebrow">Counties & regions</div>
                <ul style={{ marginTop: 12, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {area.counties.map((c) => (
                    <li key={c} style={{ fontSize: 14, color: "var(--exec-primary)" }}>· {c}</li>
                  ))}
                </ul>
              </div>
            )}
            {(!area.counties || area.counties.length === 0) && (
              <div style={{ marginTop: 20, padding: "12px 16px", background: "var(--exec-bg-alt)", borderRadius: 8, fontSize: 14, color: "var(--exec-body)" }}>
                Statewide coverage across {area.state_name}.
              </div>
            )}
          </div>

          <div>
            <div className="erf-public-eyebrow">Contact for {area.state_name}</div>
            <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <Phone size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Office</div>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>{content.contact_phone_office}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <Mail size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Email</div>
                  <div style={{ fontSize: 15, fontWeight: 500 }}>{content.contact_email}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <MapPin size={20} color="var(--exec-accent)" />
                <div>
                  <div style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--exec-body)" }}>Coverage tier</div>
                  <div style={{ fontSize: 15, fontWeight: 500, textTransform: "capitalize" }}>{area.tier} service area</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="erf-public-section-alt">
        <div className="erf-public-container">
          <div className="erf-public-section-head">
            <div className="erf-public-section-head-copy">
              <div className="erf-public-eyebrow">Services in {area.state_name}</div>
              <h2>What we deliver in {area.state_name}.</h2>
            </div>
            <Link to="/services" className="erf-public-cta-outline erf-public-section-head-cta">All services</Link>
          </div>
          <div className="erf-public-services-grid">
            {featured.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="erf-public-service-card" data-testid={`state-service-${s.slug}`}>
                <CapabilityTile service={s} alt={`${s.name} — ERF ENTERPRISES LLC`} />
                <div className="erf-public-service-card-body">
                  <h3>{s.name}</h3>
                  <p>{s.short_description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
